import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle,
  WarningAmber,
  PointOfSale,
  Lock,
  LockOpen,
  WhatsApp,
  OpenInNew
} from '@mui/icons-material';
import { User } from '../../types';
import { ZERDE_PRIMARY } from './types';

interface UserDetailHeaderProps {
  user: User;
  displayName: string;
  /** users.toggle_status в зоне оператора; статус виден всегда, без права кнопки заблокированы */
  canToggleStatus: boolean;
  /** users.impersonate: сервер не выставляет флаг для себя и для равных и старших рангов */
  canImpersonate: boolean;
  toggleBlockedReason: string;
  deactivation: number;
  pos: number;
  reports: number;
  isUserphoneWA: boolean;
  isWorkPhoneWA: boolean;
  displayUserphone: string | null;
  displayWorkPhone: string | null;
  userphone: string;
  workPhone: string;
  waMenuAnchor: HTMLElement | null;
  setWaMenuAnchor: (el: HTMLElement | null) => void;
  onToggleActivation: () => void;
  onTogglePOS: () => void;
  onToggleReports: () => void;
  /** Идет вход под сотрудником: кнопка заблокирована до перехода в его интерфейс */
  impersonating: boolean;
  onImpersonate: () => void;
  onClose: () => void;
}

export const UserDetailHeader: React.FC<UserDetailHeaderProps> = ({
  user,
  displayName,
  canToggleStatus,
  canImpersonate,
  toggleBlockedReason,
  deactivation,
  pos,
  reports,
  isUserphoneWA,
  isWorkPhoneWA,
  displayUserphone,
  displayWorkPhone,
  userphone,
  workPhone,
  waMenuAnchor,
  setWaMenuAnchor,
  onToggleActivation,
  onTogglePOS,
  onToggleReports,
  impersonating,
  onImpersonate,
  onClose
}) => {
  return (
    <Box
      sx={{
        px: { xs: 2.5, sm: 3.5 },
        py: 2.2,
        bgcolor: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
      }}
    >
      {/* Левая часть: Кнопка закрытия + ФИО */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
        <IconButton
          onClick={onClose}
          sx={{
            bgcolor: '#eff6ff',
            color: ZERDE_PRIMARY,
            border: '1px solid #bfdbfe',
            width: 36,
            height: 36,
            borderRadius: '50%',
            '&:hover': { bgcolor: '#dbeafe' }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box>
          <Typography
            variant="h5"
            sx={{
              color: '#0f172a',
              fontWeight: 700,
              fontSize: { xs: '1.2rem', sm: '1.35rem' },
              lineHeight: 1.2
            }}
          >
            {displayName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem' }}>
            Логин: <strong style={{ color: '#1e293b' }}>{user.username}</strong> • ID: <strong>#{user.id}</strong>
          </Typography>
        </Box>
      </Box>

      {/* Правая часть: Быстрые переключатели и действия */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title={!canToggleStatus ? toggleBlockedReason : ''}>
          <span>
            <Button
              size="small"
              onClick={onToggleActivation}
              disabled={!canToggleStatus}
              startIcon={
                deactivation === 0 ? (
                  <CheckCircle sx={{ fontSize: 16 }} />
                ) : (
                  <WarningAmber sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                border: '1px solid',
                borderColor: deactivation === 0 ? '#a7f3d0' : '#fed7aa',
                bgcolor: deactivation === 0 ? '#ecfdf5' : '#fff7ed',
                color: deactivation === 0 ? '#059669' : '#c2410c',
                fontSize: '0.78rem',
                fontWeight: 600,
                px: 1.6,
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                opacity: !canToggleStatus ? 0.65 : 1,
                '&:hover': {
                  bgcolor: deactivation === 0 ? '#d1fae5' : '#ffedd5'
                }
              }}
            >
              {deactivation === 0 ? 'Активен' : 'Деактивирован'}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={!canToggleStatus ? toggleBlockedReason : ''}>
          <span>
            <Button
              size="small"
              onClick={onTogglePOS}
              disabled={!canToggleStatus}
              startIcon={<PointOfSale sx={{ fontSize: 16 }} />}
              sx={{
                border: '1px solid',
                borderColor: pos === 1 ? '#a7f3d0' : '#fecaca',
                bgcolor: pos === 1 ? '#ecfdf5' : '#fef2f2',
                color: pos === 1 ? '#059669' : '#dc2626',
                fontSize: '0.78rem',
                fontWeight: 600,
                px: 1.6,
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                opacity: !canToggleStatus ? 0.65 : 1,
                '&:hover': {
                  bgcolor: pos === 1 ? '#d1fae5' : '#fee2e2'
                }
              }}
            >
              Касса: {pos === 1 ? 'Вкл' : 'Выкл'}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={!canToggleStatus ? toggleBlockedReason : pos === 0 ? 'Сначала включите кассу' : ''}>
          <span>
            <Button
              size="small"
              onClick={onToggleReports}
              disabled={!canToggleStatus || pos === 0}
              startIcon={reports === 1 ? <LockOpen sx={{ fontSize: 16 }} /> : <Lock sx={{ fontSize: 16 }} />}
              sx={{
                border: '1px solid',
                borderColor: reports === 1 ? '#a7f3d0' : '#e2e8f0',
                bgcolor: reports === 1 ? '#ecfdf5' : '#f8fafc',
                color: reports === 1 ? '#059669' : '#64748b',
                fontSize: '0.78rem',
                fontWeight: 600,
                px: 1.6,
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                opacity: !canToggleStatus || pos === 0 ? 0.4 : 1,
                '&:hover': {
                  bgcolor: reports === 1 ? '#d1fae5' : '#f1f5f9'
                }
              }}
            >
              Отчеты: {reports === 1 ? 'Вкл' : 'Выкл'}
            </Button>
          </span>
        </Tooltip>

        {(isUserphoneWA || isWorkPhoneWA) && (
          <>
            <Button
              size="small"
              startIcon={<WhatsApp sx={{ fontSize: 17 }} />}
              onClick={(e) => {
                if (isUserphoneWA && isWorkPhoneWA && displayUserphone !== displayWorkPhone) {
                  setWaMenuAnchor(e.currentTarget);
                } else {
                  const target = isUserphoneWA ? userphone : workPhone;
                  window.open(`https://wa.me/${target.replace(/\D/g, '').replace(/^8/, '7')}`, '_blank');
                }
              }}
              sx={{
                bgcolor: '#25d366',
                color: '#ffffff',
                fontSize: '0.78rem',
                fontWeight: 600,
                px: 1.6,
                height: 34,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 2px 6px rgba(37, 211, 102, 0.25)',
                '&:hover': { bgcolor: '#20ba5a' }
              }}
            >
              WhatsApp
            </Button>

            {isUserphoneWA && isWorkPhoneWA && displayUserphone !== displayWorkPhone && (
              <Menu
                anchorEl={waMenuAnchor}
                open={Boolean(waMenuAnchor)}
                onClose={() => setWaMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: { borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 200 }
                }}
              >
                <MenuItem
                  onClick={() => {
                    window.open(`https://wa.me/${userphone.replace(/\D/g, '').replace(/^8/, '7')}`, '_blank');
                    setWaMenuAnchor(null);
                  }}
                  sx={{ gap: 1.2, py: 1 }}
                >
                  <WhatsApp sx={{ fontSize: 18, color: '#25d366' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                      Личный номер
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      {displayUserphone}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    window.open(`https://wa.me/${workPhone.replace(/\D/g, '').replace(/^8/, '7')}`, '_blank');
                    setWaMenuAnchor(null);
                  }}
                  sx={{ gap: 1.2, py: 1 }}
                >
                  <WhatsApp sx={{ fontSize: 18, color: '#25d366' }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                      Рабочий телефон
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      {displayWorkPhone}
                    </Typography>
                  </Box>
                </MenuItem>
              </Menu>
            )}
          </>
        )}

        {canImpersonate && (
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
            onClick={onImpersonate}
            disabled={impersonating}
            sx={{
              bgcolor: '#4f46e5',
              color: '#ffffff',
              fontSize: '0.78rem',
              fontWeight: 700,
              px: 1.8,
              height: 34,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)',
              '&:hover': {
                bgcolor: '#4338ca'
              }
            }}
          >
            {impersonating ? 'Вход...' : 'Войти под пользователем'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UserDetailHeader;
